import { debounce } from "lodash";
import { useState } from "react";
import { logEvent } from "@src/appV2/lib/analytics";
import { ADMIN_EVENTS } from "../../../constants/analyticsEvents";
import { useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { useRecentlyVisited } from "@src/appV2/lib/utils/useRecentlyVisited";

export const AGENTS_LIMIT_FOR_LOAD = 5;
export const FACILITIES_LIMIT_FOR_LOAD = 5;

interface AccountSwitcherUser {
  user: {
    userId: string;
    name?: string;
  };
  type: string;
}

interface UseAccountSwitcherHookParams {
  /**
   * Required: the bound `pseudo` session action. Typing it as required (rather
   * than the previous implicit `any`) enforces the contract at this
   * account-switch boundary — a caller that forgets to wire it up fails at
   * compile time instead of throwing a runtime "Cannot destructure property
   * 'pseudo' of 'undefined'" deep inside the component tree (STAFF-1342).
   */
  pseudo: (data: { userId: string }) => unknown;
  closeMenu?: () => void;
}

export const useAccountSwitcherHook = ({ pseudo, closeMenu }: UseAccountSwitcherHookParams) => {
  const [searchName, setSearchName] = useState();
  const [accountsListVisible, setAccountsListVisible] = useState(false);
  const [_, addToRecent] = useRecentlyVisited();

  const {
    data: searchUsersResponse,
    isLoading: isSearchUsersLoading,
    fetchNextPage: fetchNextPageSearchUsers,
    fetchStatus: fetchStatusSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  } = useSearchAgents({
    searchInput: searchName,
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const {
    data: searchFacilitiesResponse,
    isLoading: isSearchFacilitiesLoading,
    fetchNextPage: fetchNextPageSearchFacilities,
    fetchStatus: fetchStatusSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  } = useSearchFacilities({
    searchInput: searchName ?? "",
    limit: AGENTS_LIMIT_FOR_LOAD,
  });

  const agentDetails = {
    list: searchUsersResponse?.pages.flatMap((page) => page.list) ?? [],
    loading: isSearchUsersLoading && fetchStatusSearchUsers !== "idle",
    fetchNextPage: fetchNextPageSearchUsers,
    isFetchingNextPage: isFetchingNextPageSearchUsers,
    hasNextPage: hasNextPageSearchUsers,
  };

  const facilityDetails = {
    list: searchFacilitiesResponse?.pages.flatMap((page) => page.list) ?? [],
    loading: isSearchFacilitiesLoading && fetchStatusSearchFacilities !== "idle",
    fetchNextPage: fetchNextPageSearchFacilities,
    isFetchingNextPage: isFetchingNextPageSearchFacilities,
    hasNextPage: hasNextPageSearchFacilities,
  };

  // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'open' implicitly has an 'any' type.
  const handleOnDropdownVisible = (open) => {
    setAccountsListVisible(open);
    if (!open) {
      return;
    }
    logEvent(ADMIN_EVENTS.OPEN_SEARCH);
  };

  const searchAccounts = debounce((name) => {
    setSearchName(name);
    logEvent(ADMIN_EVENTS.FILTER_SEARCH, { user_input: name });
  }, 800);

  const handleChange = ({ user, type }: AccountSwitcherUser) => {
    pseudo({ userId: user.userId });
    if (type !== "ADMIN") {
      addToRecent({ id: user.userId, title: user.name ?? "", type });
    }

    if (closeMenu) {
      closeMenu();
    }
    if (type === "AGENT") {
      logEvent(ADMIN_EVENTS.SELECT_HCP, {
        id: user.userId,
      });
    } else if (type === "FACILITY") {
      logEvent(ADMIN_EVENTS.SELECT_HCF, {
        id: user.userId,
      });
    }
    setAccountsListVisible(false);
  };
  return {
    handleChange,
    searchAccounts,
    handleOnDropdownVisible,
    accountsListVisible,
    facilityDetails,
    agentDetails,
    setAccountsListVisible,
  };
};
